<template>
  <div class="px py-small">
    <ak-box
      :background="!loading && dataList.length > 0 ? 'white' : ''"
      rounded
      size="tiny"
      class="px-none"
    >
      <ak-table
        v-model:data="dataList"
        v-model:current-page="currentPage"
        v-model:checked-rows="checked"
        :loading="loading"
        :total="total"
        :sticky-pagination="false"
        class="one-whole"
        spacing="medium"
        sortable
        inset
        backend-pagination
        checkable
        paginated
        hoverable
        :background="!loading && dataList.length > 0"
        @page="onPageChange"
      >
        <template #default="{ row }">
          <ak-table-column :label="$t('Name')" class="cursor-pointer p-0">
            <router-link
              class="block p-small"
              :to="{ name: 'automationEdit', params: { id: row._id } }"
            >
              <strong>{{ row.name }}</strong>
              <template v-if="row.description">
                <br />
                <small>{{ row.description }}</small>
              </template>
            </router-link>
          </ak-table-column>
          <ak-table-column :label="$t('Folder')" class="cursor-pointer p-0">
            <router-link
              class="flex flex--center p-small"
              :to="{ name: 'automationEdit', params: { id: row._id } }"
            >
              <ak-icon class="mr-tiny" type="folder" />
              <span class="flex__item min-width-100">{{
                row.folder || '-'
              }}</span>
            </router-link>
          </ak-table-column>
          <ak-table-column label="" class="cursor-pointer p-0">
            <router-link
              class="block p-small"
              :to="{ name: 'automationEdit', params: { id: row._id } }"
            >
              <ak-tag
                v-if="row.hidden"
                nowrap
                color="warning"
                class="pull-right"
              >
                Hidden
              </ak-tag>
            </router-link>
          </ak-table-column>
          <ak-table-column label="Enabled" class="p-0">
            <router-link
              class="block p-small"
              :to="{ name: 'automationEdit', params: { id: row._id } }"
            >
              <ak-switch
                v-model="row.status"
                true-value="enabled"
                false-value="disabled"
                @click.stop="toggle(row)"
              />
            </router-link>
          </ak-table-column>
          <ak-table-column :label="$t('Triggered')" class="cursor-pointer">
            <router-link
              class="block p-small"
              :to="{ name: 'automationEdit', params: { id: row._id } }"
            >
              <strong>
                {{ row.triggered ? formatNumber(row.triggered) : 0 }}
              </strong>
              times
            </router-link>
          </ak-table-column>
          <ak-table-column mini :label="$t(' ')">
            <automation-actions :automation="row" />
          </ak-table-column>
        </template>
        <template #empty>
          <div class="p-huge text-center">
            <ak-icon type="gears" size="huge" />
            <h3 class="color-grey">
              {{ $t("There aren't any automations here yet") }}
            </h3>
            <ak-button tag="router-link" :to="{ name: 'automationNew' }">
              {{ $t('Create Automation') }}
            </ak-button>
          </div>
        </template>
      </ak-table>
    </ak-box>
  </div>
</template>

<script>
import { defineComponent, watch, ref } from 'vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import Switch from '@akerolabs/akero-vue/src/components/switch/Switch.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import AutomationActions from './AutomationActions.vue'
import { formatNumber } from '../../../utils/helpers'
import { useRouter } from '../../../legacy/router'

export default defineComponent({
  name: 'ListView',
  components: {
    [Box.name]: Box,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Switch.name]: Switch,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Tag.name]: Tag,
    [AutomationActions.name]: AutomationActions,
  },
  props: {
    /**
     * @type {Array}
     * @description The automation data to display
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Number}
     * @description The total number of automations
     */
    total: {
      type: Number,
      default: 0,
    },
    /**
     * @type {Array}
     * @description The currently selected automations to be used in bulk actions
     */
    selected: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Boolean}
     * @description Is loading
     */
    loading: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {Number}
     * @description The current page in the pagination
     */
    activePage: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update:data', 'update:selected', 'page', 'toggle'],
  setup(props, { emit }) {
    const router = useRouter()

    const dataList = ref(props.data)
    const currentPage = ref(props.activePage)
    const checked = ref([])

    // We are getting the table data from the parent so
    // we don't have to reload the data when we switch views

    // Watch props to update internals
    watch(
      () => props.data,
      (value) => {
        dataList.value = value
      },
      { deep: true },
    )
    watch(
      () => props.selected,
      (value) => {
        checked.value = value
      },
      { deep: true },
    )
    watch(
      () => props.activePage,
      (value) => {
        currentPage.value = value
      },
    )

    // Watch internals to update props
    watch(
      dataList,
      (value) => {
        emit('update:data', value)
      },
      { deep: true },
    )
    watch(
      checked,
      (value) => {
        emit('update:selected', value)
      },
      { deep: true },
    )

    /**
     * @description when the pagination page change
     * emit the event so a fetch can be run
     * @param {Number} p
     */
    const onPageChange = (p) => {
      emit('page', p)
    }

    /**
     * @description when the enable switch is toggled
     * emit the event so the api request can be made
     * @param {Object} row
     */
    const toggle = (row) => {
      emit('toggle', row)
    }

    /**
     * @param {{_id: String}} _id
     * @description Go to the edit screen for the given automation
     */
    const editAutomation = ({ _id }) => {
      router.push('automationEdit', { id: _id })
    }

    return {
      dataList,
      currentPage,
      checked,
      formatNumber,
      onPageChange,
      toggle,
      editAutomation,
    }
  },
})
</script>

<i18n>
{
  "en": {
    "Name": "Name",
    "Folder": "Folder",
    "Enabled": "Enabled",
    "Triggered": "Triggered",
    "times": "times",
    "There aren't any automations here yet": "There aren't any automations here yet",
    "Create Automation": "Create Automation",
  },
  "en-US": {
    "Name": "Name",
    "Folder": "Folder",
    "Enabled": "Enabled",
    "Triggered": "Triggered",
    "times": "times",
    "There aren't any automations here yet": "There aren't any automations here yet",
    "Create Automation": "Create Automation",
  }
}
</i18n>
